import React, { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import logoConlicitaBranca from 'assets/images/logo-conlicita-branca.svg';
import { submitProgramaIntegridadeForm } from 'services/actions/programa_integridade_actions';
import { Container } from './styles';

interface ProgramaIntegridadeFormState {
  nome: string;
  email: string;
  telefone: string;
  nomeEmpresa: string;
  cnpj: string;
  observacao: string;
}

const INITIAL_FORM_STATE: ProgramaIntegridadeFormState = {
  nome: '',
  email: '',
  telefone: '',
  nomeEmpresa: '',
  cnpj: '',
  observacao: '',
};

const ProgramaDeIntegridade: React.FC = () => {
  const [form, setForm] = useState<ProgramaIntegridadeFormState>(INITIAL_FORM_STATE);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  useEffect(() => {
    document.title = 'Programa de Integridade - ConLicitação';
  }, []);

  const handleChange = (field: keyof ProgramaIntegridadeFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await submitProgramaIntegridadeForm({
        nome: form.nome.trim(),
        email: form.email.trim().toLowerCase(),
        telefone: form.telefone.trim(),
        nome_empresa: form.nomeEmpresa.trim(),
        cnpj: form.cnpj.trim(),
        observacao: form.observacao.trim(),
      });

      if (response?.success) {
        setSubmitted(true);
        return;
      }

      setSubmitError(response?.message || 'Não foi possível enviar o formulário. Tente novamente.');
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      setSubmitError(
        typeof apiMessage === 'string' && apiMessage
          ? apiMessage
          : 'Erro ao se comunicar com o servidor. Tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container programa-integridade-page">
      <Container>
        <div className="programa-integridade-green-bg" aria-hidden="true" />

        <div className="container programa-integridade-inner">
          <div className="row">
            <div className="col-12 col-lg-6 programa-integridade-copy-col">
              <h1 className="programa-integridade-title">
                Garanta seu Programa de Integridade para a Licitação
              </h1>
              <div className="programa-integridade-text-block">
                <p className="programa-integridade-text">
                  Se você chegou até aqui, é porque a oportunidade que deseja participar exige a
                  apresentação de um <strong>Programa de Integridade</strong> como condição para o
                  vencedor.
                </p>
                <p className="programa-integridade-text">
                  O Jurídico do ConLicitação conta com uma equipe especializada que atua na
                  orientação e implantação de Programas de Integridade, alinhados à legislação
                  vigente e às exigências dos editais, oferecendo segurança jurídica e agilidade ao
                  processo.
                </p>
                <p className="programa-integridade-text">
                  Preencha o formulário ao lado para que nosso time entre em contato e compreenda sua
                  necessidade de forma personalizada.
                </p>
              </div>
            </div>

            <div className="col-12 col-lg-6 programa-integridade-form-col">
              <div className="programa-integridade-form-card">
                {submitted ? (
                  <div className="alert alert-success mb-0" role="status">
                    Recebemos suas informações! Em breve nossa equipe entrará em contato.
                  </div>
                ) : (
                  <form className="programa-integridade-form" onSubmit={handleSubmit}>
                    {submitError && (
                      <div className="alert alert-danger mb-0" role="alert">
                        {submitError}
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="programa-integridade-nome">Nome</label>
                      <input
                        id="programa-integridade-nome"
                        type="text"
                        className="form-control"
                        placeholder="Digite o nome"
                        value={form.nome}
                        onChange={(e) => handleChange('nome', e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group col-8">
                        <label htmlFor="programa-integridade-email">E-mail</label>
                        <input
                          id="programa-integridade-email"
                          type="email"
                          className="form-control"
                          placeholder="Digite o e-mail"
                          value={form.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                        />
                      </div>
                      <div className="form-group col-4">
                        <label htmlFor="programa-integridade-telefone">Telefone</label>
                        <InputMask
                          id="programa-integridade-telefone"
                          type="tel"
                          className="form-control"
                          mask="(99) 99999-9999"
                          placeholder="Digite o telefone com DDD"
                          value={form.telefone}
                          onChange={(e) => handleChange('telefone', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="programa-integridade-empresa">Nome da empresa</label>
                      <input
                        id="programa-integridade-empresa"
                        type="text"
                        className="form-control"
                        placeholder="Digite o nome da empresa"
                        value={form.nomeEmpresa}
                        onChange={(e) => handleChange('nomeEmpresa', e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="programa-integridade-cnpj">CNPJ da empresa</label>
                      <InputMask
                        id="programa-integridade-cnpj"
                        type="text"
                        className="form-control"
                        mask="**.***.***/****-99"
                        placeholder="Digite o CNPJ da empresa"
                        value={form.cnpj}
                        onChange={(e) => handleChange('cnpj', e.target.value.toUpperCase())}
                        required
                      />
                    </div>

                    <div className="form-group programa-integridade-form-observacao">
                      <label htmlFor="programa-integridade-observacao">Observação</label>
                      <textarea
                        id="programa-integridade-observacao"
                        className="form-control"
                        rows={4}
                        placeholder="Digite a observação"
                        value={form.observacao}
                        onChange={(e) => handleChange('observacao', e.target.value)}
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary programa-integridade-submit"
                      disabled={submitting}
                    >
                      {submitting ? 'Enviando...' : 'Enviar'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>

        <footer className="programa-integridade-footer">
          <img src={logoConlicitaBranca} alt="ConLicitação" />
        </footer>
      </Container>
    </div>
  );
};

export default ProgramaDeIntegridade;
